/**
 * The Barkeep stakes you.
 *
 * ============================== WHAT THIS IS FOR ===============================
 * Buster Bucks are practice money. They refill. There is nothing to buy. This dialog
 * is where the product says that out loud, at the one moment a player might otherwise
 * think they had run out of something — so it is deliberately unexciting: one
 * sentence, one button, no countdown, no "claim your bonus", no streak.
 *
 * The rule behind it is in `lib/wallet.ts` on the server: below 500 the Barkeep stakes
 * you to 1,000, with no daily cap and no cooldown. Being broke is the only condition,
 * and being broke is self-limiting in a way a cooldown is not.
 *
 * ------------------------------ AND ONE LINE OF PITCH --------------------------
 * Master plan §7 lists the Barkeep's dialog as a marketing surface, with the rules
 * that go with it: one line, one tap, about what just happened to THIS player, never
 * during a hand, no scarcity. A player who is here because they went broke exploring is
 * told nothing at all.
 *
 * ================== PHASE 5, BOX 6: THE LINE READS POTS, NOT SITTINGS ==========
 * It used to count sittings — "three sittings lost in a row" — because in Phase 4 that
 * was the only thing the wallet ledger could see. The Phase 4 review said so at the
 * time (question 7: "a sitting is the right unit UNTIL Phase 5's Ledger carries pots;
 * then the Ledger feeds the Barkeep's line"), and this is that.
 *
 * A count of sittings is a fact about the wallet. A pot is a fact about the table, and
 * it is the one the player remembers: they did not go broke over three sittings, they
 * went broke in a hand, and they can usually tell you which one. So the line names the
 * biggest pot they lost and the street the most EV went on, and only then the drill.
 *
 * `getLastSitting()` is where those two ride — on the DEVICE, written by the Ledger
 * when the sitting ends. `prefs.ts` carries the full argument for why they are not in
 * the session POST; the short version is that the Worker is closed this phase and would
 * neither store them nor hand them back, so a field there would be write-only.
 *
 * The old sittings count is KEPT as the fallback. A player whose last sitting predates
 * this build has no `lastSitting` record and the wallet ledger still knows they have
 * been losing, and that is still worth one line.
 * ===============================================================================
 */
import { Link } from "react-router-dom";
import { BucksIcon } from "../lib/mark";
import { useWallet } from "../lib/WalletContext";
import { PLAY_CARDS, TRAIN_CARDS } from "./poker-cards";
import { getLastCard, getLastSitting, type LastSitting } from "../settings/prefs";
import { drillFor } from "./copy";
import "./rooms.css";

/**
 * How many sittings in a row ended down.
 *
 * A sitting is a `buyin` and the `settle` that answers it, so the ledger is read
 * newest-first in pairs: a settle worth less than the buy-in before it is a losing
 * sitting. A buy-in with no settlement after it is the sitting in progress and is not
 * counted — nothing has happened to it yet.
 */
export function losingRun(entries: { delta: number; reason: string }[]): number {
  let run = 0;
  for (let i = 0; i < entries.length - 1; i++) {
    const a = entries[i]!;
    const b = entries[i + 1]!;
    if (a.reason !== "settle" || b.reason !== "buyin") continue;
    if (a.delta < Math.abs(b.delta)) run += 1;
    else break;
  }
  return run;
}

/**
 * The one line, from the last sitting's pots.
 *
 * Returns null when there is nothing true to say, which is the important half of the
 * contract: a player who went broke over four hands they were never in gets a stake and
 * no lecture. Something is said only when the sitting actually lost a pot or gave up EV
 * somewhere nameable.
 */
export function potLine(s: LastSitting | null): { text: string; drill: string } | null {
  if (!s || s.hands <= 0 || s.net >= 0) return null;
  /* The whole shape, not just the card: a Custom table names no card, and the shape is
     the only thing left that knows whether it was a bubble. */
  const drill = drillFor({ cardId: s.cardId, seats: s.seats, stackBb: s.stackBb, format: s.format });
  if (s.biggestLostPot && s.biggestLostPot > 0) {
    /*
     * The unit, said out loud.
     *
     * At a cash table a chip IS a Buck, and this dialog is about Bucks — so an
     * unlabelled number there reads correctly. At a TOURNAMENT it does not: those
     * chips are explicitly not Bucks, which is the only footing on which the seat may
     * take Bucks at all, and printing "4,850" in the Barkeep's own dialog would be the
     * one place in the product that blurred the two.
     */
    const n = Math.round(s.biggestLostPot).toLocaleString("en-US");
    const unit = s.format === "tournament" ? `${n} chips` : `${n} Bucks`;
    return { text: `The biggest pot you lost last time was ${unit}.`, drill: drill.id };
  }
  if (s.worstSpot) {
    return { text: `Most of what you gave up last time went ${s.worstSpot.label}.`, drill: drill.id };
  }
  return null;
}

export function Barkeep({ onClose }: { onClose: () => void }) {
  const { balance, stakeTo, floor, canStake, guest, recent, stake } = useWallet();
  const run = losingRun(recent);
  const card = PLAY_CARDS.find((c) => c.id === getLastCard());
  const drill = TRAIN_CARDS.find((t) => t.id === (card?.drill ?? ""));
  /* Pots first; the sittings count is the fallback for a player from an older build. */
  const pot = potLine(getLastSitting());
  const potDrill = pot ? TRAIN_CARDS.find((t) => t.id === pot.drill) : null;

  return (
    /*
     * ITS OWN SCRIM, not the Settings sheet's.
     *
     * The first version reused `.sheet`, which is a bottom sheet on a phone and a
     * full-height SIDE PANEL on a desktop — so the Barkeep's four lines of copy came
     * out as a column of empty panel two thirds of the screen tall. A sheet is for a
     * list of settings you scroll; this is one sentence and one button, and the shape
     * for that is a small centred card.
     */
    <div className="barkeep-scrim" role="presentation" onClick={onClose}>
      <section className="barkeep" role="dialog" aria-modal="true"
               aria-label="The Barkeep" onClick={(e) => e.stopPropagation()}>
        <div className="barkeep-coin" aria-hidden="true"><BucksIcon /></div>

        {canStake ? (
          <>
            <p className="barkeep-line">
              You're down to <b className="num">{balance.toLocaleString("en-US")}</b>. The Barkeep
              will stake you to <b className="num">{stakeTo.toLocaleString("en-US")}</b> — it's
              practice money, it refills.
            </p>
            <button className="btn barkeep-go" type="button"
                    onClick={() => { void stake(); onClose(); }}>
              Take the stake
            </button>
          </>
        ) : (
          <>
            <p className="barkeep-line">
              You have <b className="num">{balance.toLocaleString("en-US")}</b> Bucks. The Barkeep
              only stakes players who are broke — come back under {floor}.
            </p>
            <button className="btn-quiet barkeep-go" type="button" onClick={onClose}>Close</button>
          </>
        )}

        <p className="barkeep-note">
          Buster Bucks are practice money. They refill. There is nothing to buy.
          {guest && " Yours live on this device; signing in starts you at 5,000."}
        </p>

        {/* §7: one line, one tap, and only when there is something true to say. */}
        {pot && potDrill ? (
          <p className="barkeep-pitch">
            {pot.text}{" "}
            <Link to={`/poker/train/run?card=${potDrill.id}`} onClick={onClose}>{potDrill.title}</Link>
            {" "}is where that stops.
          </p>
        ) : run >= 2 && drill ? (
          <p className="barkeep-pitch">
            {run === 2 ? "Two" : run === 3 ? "Three" : String(run)} sittings lost in a row.{" "}
            <Link to="/poker/train" onClick={onClose}>{drill.title}</Link> is where that stops.
          </p>
        ) : null}
      </section>
    </div>
  );
}
